import type {CopilotChatMessage} from '@github-ui/copilot-chat-types'
import type {PreviewableContent} from '@github-ui/copilot-immersive-v1/components/ContentPreview/content-preview-types'
import {useContentPreview} from '@github-ui/copilot-immersive-v1/components/ContentPreview/ContentPreviewContext'
import {sendEvent} from '@github-ui/hydro-analytics'
import {ActionList} from '@primer/react'
import {downloadZip} from 'client-zip'
import snakeCase from 'lodash-es/snakeCase'
import {useCallback} from 'react'

import {useChatManager} from '../../../utils/CopilotChatManagerContext'

export type VersionedFilesMap = Map<string, Array<PreviewableContent & {version: number}>>

const FILE_TYPE_CONFIGS = {
  json: {
    name: 'JSON',
    mimeType: 'application/json',
    exportHandler: generateJsonExport,
  },
  md: {
    name: 'Markdown',
    mimeType: 'text/markdown',
    exportHandler: generateMarkdownExport,
  },
} as const

function threadUrl(threadId: string): string {
  return `${window.location.origin}/copilot/c/${threadId}`
}

function filterMessageFields(message: CopilotChatMessage): Partial<CopilotChatMessage> {
  // Only include relevant fields for export
  return {
    id: message.id,
    parentMessageID: message.parentMessageID,
    intent: message.intent,
    role: message.role,
    model: message.model,
    content: message.content,
    createdAt: message.createdAt,
    threadID: message.threadID,
    references: message.references,
    skillExecutions: message.skillExecutions,
  }
}

export function formatMessagesAsJson(
  messages: readonly CopilotChatMessage[],
  files: VersionedFilesMap,
  currentUserLogin: string,
  threadId: string,
  threadName: string,
): string {
  const filteredMessages = messages.map(filterMessageFields)

  const filesObject = Object.fromEntries(files)

  const metaData = {
    currentUserLogin,
    threadUrl: threadUrl(threadId),
    threadName,
    messages: filteredMessages,
    files: filesObject,
  }

  return JSON.stringify(metaData, null, 2)
}

export function generateJsonExport(
  messages: readonly CopilotChatMessage[],
  files: VersionedFilesMap,
  currentUserLogin: string,
  threadId: string,
  threadName: string,
): {url: string; exportName: string} {
  const jsonData = formatMessagesAsJson(messages, files, currentUserLogin, threadId, threadName)

  const blob = new Blob([jsonData], {type: FILE_TYPE_CONFIGS['json'].mimeType})
  const url = URL.createObjectURL(blob)

  return {url, exportName: `${fileName(threadName)}.json`}
}

export function formatMessagesAsMarkdown(
  messages: readonly CopilotChatMessage[],
  currentUserLogin: string,
  threadId: string,
  threadName: string,
): string {
  const filteredMessages = messages.filter(message => message.id !== 'root')

  // Create a map grouped by parentMessageID with child count
  const parentMap = new Map<string, {childMessageCount: number}>()
  for (const message of filteredMessages) {
    const parentId = message.parentMessageID
    if (parentId) {
      if (!parentMap.has(parentId)) {
        parentMap.set(parentId, {
          childMessageCount: 0,
        })
      }
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      const entry = parentMap.get(parentId)!
      entry.childMessageCount += 1
    }
  }

  const metaData = `# Copilot Chat Conversation Export: ${threadName}

**User:** @${currentUserLogin}
**Thread URL:** ${threadUrl(threadId)}`

  const formattedMessages = filteredMessages
    .map(message => {
      const role = message.role === 'user' ? `## @${currentUserLogin}` : '## Copilot'
      const content = message.content || ''

      let messageContent = `${role}\n\n${content}\n`

      // Add system message to subthreadded messages
      if (message.parentMessageID) {
        const parentMessage = parentMap.get(message.parentMessageID)

        if (parentMessage && parentMessage.childMessageCount > 1) {
          messageContent += `\n*System: User retried message*\n`
        }
      }

      return messageContent
    })
    .join('\n')

  return `${metaData}\n\n${formattedMessages}`
}

export async function generateMarkdownExport(
  messages: readonly CopilotChatMessage[],
  files: VersionedFilesMap,
  currentUserLogin: string,
  threadId: string,
  threadName: string,
): Promise<{url: string; exportName: string}> {
  const markdownChatMessages = formatMessagesAsMarkdown(messages, currentUserLogin, threadId, threadName)
  const markdownChatFile = new File([markdownChatMessages], `${fileName(threadName)}.md`)

  const content = [markdownChatFile]

  if (files.size > 0) {
    for (const [, versions] of files) {
      for (const item of versions) {
        if ('value' in item && typeof item.value === 'string') {
          content.push(new File([item.value], `v${item.version}_${item.name}`))
        }
      }
    }
    const blob = await downloadZip(content).blob()
    const url = URL.createObjectURL(blob)
    return {url, exportName: `${fileName(threadName)}.zip`}
  } else {
    const blob = new Blob(content, {type: FILE_TYPE_CONFIGS['md'].mimeType})
    const url = URL.createObjectURL(blob)
    return {url, exportName: `${fileName(threadName)}.md`}
  }
}

function fileName(threadName: string): string {
  return snakeCase(`${new Date().toDateString()} ${threadName}`)
}

function sendTelemetryEvent(target: string) {
  sendEvent('dotcom_chat.activate', {
    target,
    mode: 'immersive',
  })
}

function hydrateVersionedFiles(
  items: ReturnType<typeof useContentPreview>['items'],
  versionedItems: ReturnType<typeof useContentPreview>['versionedItems'],
): VersionedFilesMap {
  const hydratedMap = new Map<string, Array<PreviewableContent & {version: number}>>()

  // Iterate through each base ID and its version IDs
  for (const [, versionIds] of versionedItems) {
    // Map each version ID to its full item data with version number
    const hydratedVersions = versionIds
      .map(versionId => items.get(versionId))
      .filter((item): item is NonNullable<typeof item> => item !== undefined)
      .map((item, index) => ({
        version: index + 1,
        ...item,
      }))

    // Only add to map if we have hydrated versions and the first item is a file
    if (hydratedVersions.length > 0 && hydratedVersions[0]?.type === 'file' && hydratedVersions[0]?.name) {
      hydratedMap.set(hydratedVersions[0].name, hydratedVersions)
    }
  }

  return hydratedMap
}

const ERROR_MESSAGE = 'There was an error exporting the conversation. Please try again.'

type FileType = keyof typeof FILE_TYPE_CONFIGS

interface ExportActionItemProps {
  fileType: FileType
  threadName: string
  threadId: string
  currentUserLogin: string
  messages: readonly CopilotChatMessage[]
}

export function ExportActionItem({fileType, threadName, threadId, currentUserLogin, messages}: ExportActionItemProps) {
  const manager = useChatManager()

  const {items, versionedItems} = useContentPreview()
  const hydratedFiles = hydrateVersionedFiles(items, versionedItems)

  const displayName = FILE_TYPE_CONFIGS[fileType].name
  const telemetryTarget = `CONVERSATION_CONTEXT_MENU_EXPORT_${displayName.toUpperCase()}`

  const handleSelect = useCallback(async () => {
    try {
      const {url, exportName} = await FILE_TYPE_CONFIGS[fileType].exportHandler(
        messages,
        hydratedFiles,
        currentUserLogin,
        threadId,
        threadName,
      )

      const a = document.createElement('a')
      a.href = url
      a.download = exportName
      a.style.display = 'none'
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)

      sendTelemetryEvent(`${telemetryTarget}_SUCCEEDED`)
      // Defer revocation to ensure the click completes
      setTimeout(() => URL.revokeObjectURL(url), 0)
    } catch {
      sendTelemetryEvent(`${telemetryTarget}_DOWNLOAD_FAILED`)
      manager.addAmbientError(ERROR_MESSAGE)
    }
  }, [fileType, messages, hydratedFiles, currentUserLogin, threadId, threadName, telemetryTarget, manager])

  return (
    <ActionList.Item aria-label={`Export conversation as ${displayName}`} onSelect={handleSelect}>
      {displayName}
    </ActionList.Item>
  )
}
